import * as React from 'react';
// Hook
import { useOtpStateBase } from '@shopee/otp-hook-base';

// API
import { apiSendOtpWrapper, apiVerifyOtp } from './api';

// Constants & Enums
import { OtpType, VcodeOperation } from '@shopee-user/otp-constants';
import {
  OTP_COUNTDOWN_TIME,
  OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT,
  AuthKeyType,
  DialogType,
  OtpTransitionState,
  UseCase,
} from '@shopee-user/otp-hook-base-constants';
import { config } from '@shopee-user/platform';
import { Env } from '@shopee-user/global';

// Types
import { VerifyOtpApiData } from './types';
import type {
  GetOtpSettingsApi,
  GetOtpSettingsApiArguments,
} from '@shopee-user/otp-hook-base-types';
import type { OtpSessionInfo } from '@shopee-user/otp-hook-base-types';
import { WhatsappType } from './constants';
import { WithUserWebViewProps } from '@shopee-user/with-web-view';
import { getWhatsappAppsInstalledList } from './utils';

const defaultCooldown =
  config.ENV === Env.Test
    ? OTP_COUNTDOWN_TIME_IN_TEST_ENVIRONMENT
    : OTP_COUNTDOWN_TIME;

type TriggerWhatsappLinkParameters = {
  authKey?: string;
  token?: string;
  captchaRequired: boolean;
  captchaAppKey?: string;
  captchaScene?: string;
  captchaSignature?: string;
  sessionInfo?: OtpSessionInfo;
};

export default function useWhatsappAuthLink({
  operation,
  triggerOtpFlow,
  proceedToWhatsappAuthLinkPage,
  webViewCheckWhatsappAvailable,
}: {
  operation: VcodeOperation;
  triggerOtpFlow: ({
    authKey,
    authKeyType,
    token,
    captchaSignature,
  }: {
    authKey?: string;
    authKeyType?: AuthKeyType;
    token?: string;
    captchaSignature?: string;
  }) => Promise<{ error?: number; error_msg?: string }>;
  proceedToWhatsappAuthLinkPage: () => void;
  webViewCheckWhatsappAvailable: WithUserWebViewProps['webViewCheckWhatsappAvailable'];
}) {
  const whatsappLinkTriggerParameters = React.useRef<
    TriggerWhatsappLinkParameters
  >();
  const [whatsappAppsInstalled, setWhatsappAppsInstalled] = React.useState<
    WhatsappType[]
  >([]);

  const [whatsappChannelId, setWhatsappChannelId] = React.useState<string>();
  '';

  const checkIsWhatsappInstalled = async () => {
    const response = await webViewCheckWhatsappAvailable();
    if (response.errorCode) {
      return false;
    }
    const appsInstalled = getWhatsappAppsInstalledList({
      hasWhatsappBusiness: response.waBizAvailable || false,
      hasWhatsappNormal: response.waConsumerAvailable || false,
    });
    setWhatsappAppsInstalled(appsInstalled);
    return appsInstalled.length > 0;
  };

  const getOtpSettingsConstructor: GetOtpSettingsApi<
    VcodeOperation,
    undefined
  > = async (_: GetOtpSettingsApiArguments<VcodeOperation>) => {
    const sessionInfo = whatsappLinkTriggerParameters.current?.sessionInfo;
    return {
      preferredChannel: OtpType.WhatsappAuthLink,
      availableChannels: [OtpType.WhatsappAuthLink],
      hiddenChannels: [],
      remainingResendCountdown:
        sessionInfo?.remaining_resend_cooldown_sec || defaultCooldown,
      latestSentChannel: sessionInfo?.latest_sent_channel,
      needCaptcha: !!whatsappLinkTriggerParameters.current?.captchaRequired,
      captchaAppKey: whatsappLinkTriggerParameters.current?.captchaAppKey || '',
      captchaScene: whatsappLinkTriggerParameters.current?.captchaScene || '',
      serviceNotAvailable: false,
      error: 0,
      error_msg: '',
    };
  };

  const { actions, ...otpStateBase } = useOtpStateBase<
    VcodeOperation,
    undefined,
    undefined,
    VerifyOtpApiData
  >({
    operation,
    supportDynamicCaptcha: true,
    supportedChannels: [OtpType.WhatsappAuthLink],
    shouldResetOtpFlowUponSuccessfulVerification: true,
    api: {
      apiGetOtpSettings: getOtpSettingsConstructor,
      apiSendOtp: apiSendOtpWrapper({ setWhatsappChannelId }),
      apiVerifyOtp,
    },
    currentUseCase: UseCase.WhatsappAuthLink,
    actions: {
      switchToAnotherUsecase: () => {
        /**
         * will be empty in WAL Wrapper
         * * Currently, WAL is unable to dynamically switch to a different flow
         * * switching to a different flow can only be triggered by user manually through CheckWhatsappPage
         * * CheckWhatsappPage will invoke externalActions.triggerOtpFlow
         */
        return { error: 0, error_msg: '' };
      },
    },
  });

  React.useEffect(() => {
    if (otpStateBase.state !== OtpTransitionState.Init) {
      proceedToWhatsappAuthLinkPage();
    }
  }, [otpStateBase.state]);

  const { beginOtpFlow, ...defaultActions } = actions;

  const beginWhatsappAuthenticationLinkFlow = async ({
    authKey,
    token,
    captchaRequired,
    captchaAppKey,
    captchaScene,
    captchaSignature,
    sessionInfo,
  }: TriggerWhatsappLinkParameters) => {
    // Set Whatsapp Auth Link Parameters
    whatsappLinkTriggerParameters.current = {
      authKey,
      token,
      captchaRequired,
      captchaAppKey,
      captchaScene,
      sessionInfo,
    };
    const { error, error_msg } = await beginOtpFlow({
      authKey,
      authKeyType: AuthKeyType.Phone,
      token,
      captchaSignature,
    });
    return { error, error_msg };
  };

  return {
    ...otpStateBase,
    actions: {
      ...defaultActions,
      beginWhatsappAuthenticationLinkFlow,
    },
    whatsappChannelId,
    whatsappAppsInstalled,
    externalActions: {
      triggerOtpFlow,
      checkIsWhatsappInstalled,
    },
    isCaptchaShown: otpStateBase.dialogState === DialogType.Captcha,
  };
}
